import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { SimpleSelect } from "./DepotField";
import { ConfirmDelete, Loader, TABLE_HEAD, TABLE_ROW } from "./common";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, money, qtyFmt, dateTimeFmt, CASH_METHODS, TZ } from "../lib/api";

const SIDES = {
  customer: {
    history: "/debtors/history", pay: "/credit-payments", nf: "customer", entries: "sales", tp: "debtor",
    creditLabel: "On credit", entriesLabel: "Credit sales", paymentsLabel: "Payments received", methodLabel: "Received via",
    deleteDesc: "The customer's balance will go back up by this amount.",
  },
  supplier: {
    history: "/creditors/history", pay: "/supplier-payments", nf: "supplier", entries: "purchases", tp: "creditor",
    creditLabel: "Bought on credit", entriesLabel: "Credit purchases", paymentsLabel: "Payments made", methodLabel: "Paid via",
    deleteDesc: "The amount you owe this supplier will go back up.",
  },
};

function PaymentForm({ side, row, balance, onPaid }) {
  const cfg = SIDES[side];
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Cash");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(cfg.pay, { [`${cfg.nf}_key`]: row[`${cfg.nf}_key`], depot_id: row.depot_id, amount: Number(amount), payment_method: method, note });
      toast.success(`Payment of ${money(amount)} recorded`);
      setAmount(""); setNote("");
      onPaid();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-3 rounded-lg border border-slate-800 bg-slate-950 p-4" data-testid={`${cfg.tp}-payment-form`}>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="label-mono">Amount (TSh)</Label>
          <button type="button" onClick={() => setAmount(String(balance))} className="text-xs text-amber-400 hover:underline" data-testid={`${cfg.tp}-pay-full-button`}>Pay full</button>
        </div>
        <Input type="number" step="0.01" min="0.01" max={balance} required value={amount} onChange={(e) => setAmount(e.target.value)} data-testid={`${cfg.tp}-payment-amount-input`} />
      </div>
      <SimpleSelect label={cfg.methodLabel} value={method} onChange={setMethod} options={CASH_METHODS} testid={`${cfg.tp}-payment-method-select`} />
      <div className="col-span-2 space-y-1.5">
        <Label className="label-mono">Note</Label>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" data-testid={`${cfg.tp}-payment-note-input`} />
      </div>
      <Button type="submit" disabled={busy} className="col-span-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid={`${cfg.tp}-payment-submit-button`}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Record payment
      </Button>
    </form>
  );
}

function DebtorDetail({ side, row, onChanged }) {
  const cfg = SIDES[side];
  const { isAdmin } = useAuth();
  const { data, reload } = useApi(cfg.history, { [`${cfg.nf}_key`]: row[`${cfg.nf}_key`], depot_id: row.depot_id, tz: TZ });
  const refresh = () => { reload(); onChanged(); };
  const removePayment = async (id) => {
    try { await api.delete(`${cfg.pay}/${id}`); toast.success("Payment deleted"); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };
  if (!data) return <Loader />;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="rounded border border-slate-800 p-3"><div className="label-mono">{cfg.creditLabel}</div><div className="font-mono text-lg">{money(data.total_credit)}</div></div>
        <div className="rounded border border-slate-800 p-3"><div className="label-mono">Paid</div><div className="font-mono text-lg text-emerald-400">{money(data.total_paid)}</div></div>
        <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3"><div className="label-mono !text-amber-400">Balance</div><div className="font-mono text-lg font-bold text-amber-300" data-testid={`${cfg.tp}-balance`}>{money(data.balance)}</div></div>
      </div>
      {data.balance > 0 && <PaymentForm side={side} row={row} balance={data.balance} onPaid={refresh} />}
      <div>
        <div className="label-mono mb-2">{cfg.entriesLabel}</div>
        <table className="w-full text-sm" data-testid={`${cfg.tp}-entries-table`}>
          <thead className={TABLE_HEAD}><tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Total</th>{side === "supplier" && <th className="px-3 py-2 text-left">Due</th>}</tr></thead>
          <tbody>{data[cfg.entries].map((m) => (
            <tr key={m.id} className={TABLE_ROW}><td className="px-3 py-2 font-mono text-xs text-slate-400">{dateTimeFmt(m.created_at)}</td><td className="px-3 py-2">{m.product_name}</td><td className="px-3 py-2 text-right font-mono">{qtyFmt(m.qty)}</td><td className="px-3 py-2 text-right font-mono">{money(m.total)}</td>{side === "supplier" && <td className="px-3 py-2 font-mono text-xs">{m.due_date || "—"}</td>}</tr>
          ))}</tbody>
        </table>
      </div>
      <div>
        <div className="label-mono mb-2">{cfg.paymentsLabel}</div>
        {!data.payments.length ? <div className="text-sm text-slate-500">No payments yet</div> : (
          <table className="w-full text-sm" data-testid={`${cfg.tp}-payments-table`}>
            <thead className={TABLE_HEAD}><tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Method</th><th className="px-3 py-2 text-left">By</th><th className="px-3 py-2 text-right">Amount</th>{isAdmin && <th />}</tr></thead>
            <tbody>{data.payments.map((p) => (
              <tr key={p.id} className={TABLE_ROW}>
                <td className="px-3 py-2 font-mono text-xs text-slate-400">{dateTimeFmt(p.created_at)}</td>
                <td className="px-3 py-2">{p.payment_method}{p.note && <span className="text-xs text-slate-500"> · {p.note}</span>}</td>
                <td className="px-3 py-2 text-xs text-slate-400">{p.user_name}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-400">{money(p.amount)}</td>
                {isAdmin && <td className="px-2"><ConfirmDelete title="Delete payment?" description={cfg.deleteDesc} onConfirm={() => removePayment(p.id)} testid={`delete-payment-${p.id}`} /></td>}
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function DebtorDialog({ row, onOpenChange, onChanged, side = "customer" }) {
  const cfg = SIDES[side];
  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${cfg.tp}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase" data-testid={`${cfg.tp}-dialog-title`}>{row?.[cfg.nf]}</DialogTitle>
          <div className="text-xs text-slate-400">{row?.depot_name}</div>
        </DialogHeader>
        {row && <DebtorDetail side={side} row={row} onChanged={onChanged} />}
      </DialogContent>
    </Dialog>
  );
}
